<script setup lang="ts">
  import { Icon, IconName, IconSize, Select, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import type { TouristRoom } from '@/entities/searchCriteria';
  import AdultsStepper from './AdultsStepper.vue';
  import { TOURISTS_TEXTS } from '../config/texts';
  import { childSelectLabel } from '../lib/formatTourists';
  import { MAX_CHILDREN_PER_ROOM } from '../lib/touristsRooms';
  import type { ChildAgeOption } from '../model/types';

  interface RoomCardProps {
    room: TouristRoom;
    index: number;
    removable: boolean;
    ageOptions: ChildAgeOption[];
    mobile?: boolean;
    isVisibleHeader?: boolean;
    fullWidth?: boolean;
  }

  const props = withDefaults(defineProps<RoomCardProps>(), {
    isVisibleHeader: true,
    mobile: false,
    fullWidth: false,
  });

  interface RoomCardEmits {
    (e: 'update:adults', value: number): void;
    (e: 'add-child', age: number): void;
    (e: 'change-child-age', payload: { childIndex: number; age: number }): void;
    (e: 'remove-child', childIndex: number): void;
    (e: 'remove-room'): void;
    (e: 'visible-ages'): void;
    (e: 'hidden-ages'): void;
    (e: 'open-age-picker'): void;
  }

  const emit = defineEmits<RoomCardEmits>();

  const canAddChild = computed<boolean>(() => props.room.childrenAges.length < MAX_CHILDREN_PER_ROOM);

  const onFocus = (): void => {
    emit('visible-ages');
  };
</script>

<template>
  <div
    :class="['tourists-room', { 'tourists-room_full-width': fullWidth }]"
    :data-testid="`tourists-rooms-room-${index}`"
  >
    <div
      v-if="isVisibleHeader"
      class="tourists-room__header"
    >
      <Typo
        class="tourists-room__title"
        :variant="TypoVariant.Heading5"
        tag="span"
      >
        {{ index + 1 }} {{ TOURISTS_TEXTS.roomWord }}
      </Typo>

      <button
        v-if="removable"
        type="button"
        class="tourists-room__remove"
        :data-testid="`tourists-rooms-room-${index}-remove`"
        :aria-label="TOURISTS_TEXTS.removeRoom"
        @click="emit('remove-room')"
      >
        <Icon
          :name="IconName.Trash"
          :size="IconSize.L"
        />
      </button>
    </div>

    <div class="tourists-room__adults">
      <div class="tourists-room__adults-label">
        <Typo
          :variant="TypoVariant.BodyLRegular"
          tag="span"
        >
          {{ TOURISTS_TEXTS.adultsLabel }}
        </Typo>
        <Typo
          class="tourists-room__adults-hint"
          :variant="TypoVariant.BodySRegular"
          tag="span"
        >
          {{ TOURISTS_TEXTS.adultsHint }}
        </Typo>
      </div>

      <AdultsStepper
        :value="room.adults"
        :testid="`tourists-rooms-room-${index}-adults`"
        @update:value="emit('update:adults', $event)"
      />
    </div>

    <div class="tourists-room__children">
      <Select
        v-for="(age, j) in room.childrenAges"
        :key="j"
        class="tourists-room__child"
        :model-value="age"
        :options="ageOptions"
        text-key="label"
        value-key="value"
        :size="mobile ? 'l' : 'm'"
        readonly-top
        :data-testid="`tourists-rooms-room-${index}-child-${j}`"
        @update:modelValue="emit('change-child-age', { childIndex: j, age: $event })"
      >
        <template #append>
          <div @click="emit('remove-child', j)">
            <Icon name="close" />
          </div>
        </template>
        <template #customValue>
          <Typo>{{ childSelectLabel(age) }}</Typo>
        </template>
      </Select>

      <template v-if="canAddChild">
        <div
          v-if="mobile"
          :data-testid="`tourists-rooms-room-${index}-add-child`"
          class="tourists-room__select-mobile"
          @click="emit('open-age-picker')"
        >
          <Typo variant="body-l-400">Добавить ребёнка</Typo>
          <Icon
            v-if="mobile"
            name="chevron-down"
            class="tourists-room__icon-chevron"
            size="l"
          />
        </div>
        <Select
          v-else
          :model-value="null"
          :options="ageOptions"
          :placeholder="TOURISTS_TEXTS.addChild"
          text-key="label"
          value-key="value"
          :size="mobile ? 'l' : 'm'"
          :data-testid="`tourists-rooms-room-${index}-add-child`"
          @click.native="emit('open-age-picker')"
          @focus="onFocus"
          @blur="emit('hidden-ages')"
          @update:modelValue="emit('add-child', $event)"
        >
          <template #prependOption>
            <Typo>
              Укажите возраст ребёнка на момент окончания поездки. Возраст влияет на параметры и цену предложения
            </Typo>
          </template>
        </Select>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .tourists-room {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-m);
    width: 307px;

    &_full-width {
      width: 100%;
    }

    &__header {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }

    &__title {
      color: var(--color-text-primary);
    }

    &__icon-chevron {
      fill: var(--color-text-secondary);
    }

    &__select-mobile {
      align-items: center;
      border: 1px solid var(--color-neutral-40);
      border-radius: var(--rounding-s);
      color: var(--color-text-secondary);
      display: flex;
      height: 48px;
      justify-content: space-between;
      padding: 0 var(--spacing-m);
    }

    &__remove {
      align-items: center;
      background: none;
      border: none;
      color: var(--color-error-500);
      cursor: pointer;
      display: inline-flex;
      justify-content: center;
      padding: 0;

      ::v-deep svg {
        fill: currentcolor;
      }
    }

    &__adults {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }

    &__adults-label {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xxxs);
      width: 96px;
    }

    &__adults-hint {
      color: var(--color-text-secondary);
    }

    &__children {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xs);
    }
  }
</style>
